// `Field` (design-system spec §3.10) — "11 px 600 `--mut` label above a 28 px
// control". The spec has mandated this primitive since the lock; until UX-3's
// surface review (F6) it did not exist, and two parallel hand-rolled grammars
// stood in for it:
//
//   * `.ds-field` — a <div> with a nested <label>, hand-written in the admin
//     console and the onboarding wizard;
//   * `.filter-chip` — a <label> with a <span> caption, defined in
//     `areas/office/inventory/inventory.css` and used 117 times across BOTH
//     office areas. Production's filter bars and the whole create-WO form were
//     styled by the INVENTORY module's stylesheet: the same cross-area
//     dependency UX-3 removed from the sub-navigation, left in place on every
//     form control one level down.
//
// This file is the primitive; the grammar it emits now lives in the design
// layer (`styles/office.css`, "§3.10 Field"), not in an area file. The class
// names `filter-chip` / `filter-chip-label` are EMITTED UNCHANGED and are not
// deprecated aliases-in-waiting: fourteen certified module-06 walkthroughs and
// M04-S01 address controls through them ("restyling never rewrites a
// walkthrough", spec §6a). What changed is where they are defined and what
// they do — see the two geometry defects below.
//
// TWO DEFECTS THE STACK-ONLY GRAMMAR CAUSED, fixed here rather than at the
// seventeen call sites:
//
//  1. `flex-direction: column` with `align-items` unset resolves to `stretch`,
//     so a CHECKBOX inside a chip stretched to the width of its row — the
//     record-result panel's "Step finished" box measured 1160 px, drawing its
//     13 px glyph in the middle of the row, nowhere near its label. A checkbox
//     is not a stacked field: `CheckboxField` is a ROW (box, then label), and
//     the box never stretches.
//  2. Controls set their own heights, so one filter row sat on three
//     baselines: selects 33 px, search inputs 31 px, an unlabelled checkbox
//     chip 39 px against labelled chips at 53 px. The grammar now fixes the
//     control box at the spec's 28 px, so a row is a row whatever is in it.
//
// The label is the element itself (`<label>` wrapping its control), so the
// caption is implicitly associated with the control — no `htmlFor`/`id` pair
// to keep in sync, and clicking the caption focuses the control.

import type { ReactNode } from "react";

export interface FieldProps {
  /** The 11 px uppercase caption. */
  label: ReactNode;
  /** The control — a `<select>`, `<input>`, `<textarea>` or a picker. */
  children: ReactNode;
  /** Wider minimum for a search box or a long select (`.filter-chip-wide`). */
  wide?: boolean;
  /** Extra classes for the call site's own layout needs. */
  className?: string;
  "data-testid"?: string;
}

/**
 * A stacked labelled control: caption above, control below, both full width of
 * the field. This is the shape for every select, text input, date input and
 * textarea in a filter bar or a form.
 */
export function Field({ label, children, wide = false, className, ...rest }: FieldProps) {
  const classes = ["ds-field", "filter-chip"];
  if (wide) classes.push("filter-chip-wide");
  if (className !== undefined) classes.push(className);
  return (
    <label className={classes.join(" ")} {...rest}>
      <span className="ds-field-label filter-chip-label">{label}</span>
      {children}
    </label>
  );
}

export interface CheckboxFieldProps {
  /** The caption, which sits AFTER the box — a checkbox reads left to right. */
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

/**
 * A boolean control. Deliberately NOT `Field` with a checkbox inside it: a
 * stacked caption over a stretched box is the defect F6 measured, and a
 * checkbox's label belongs beside its box, not above it. The box is
 * `flex: none` so it stays 13 px however wide the row is.
 *
 * `data-testid` rides the INPUT, not the wrapper: a test drives the control it
 * is naming (`fireEvent.click`, Playwright's `.check()`), and a click on the
 * label is a different event that only reaches the box through the browser's
 * label-activation behaviour.
 */
export function CheckboxField({
  label,
  checked,
  onChange,
  disabled = false,
  className,
  ...rest
}: CheckboxFieldProps) {
  const classes = ["ds-field-check", "filter-chip", "filter-chip-check"];
  if (className !== undefined) classes.push(className);
  return (
    <label className={classes.join(" ")}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
        {...rest}
      />
      <span>{label}</span>
    </label>
  );
}
